import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchMyProfile } from '@shared/api/auth';
import { refreshAccessToken } from '@shared/api/client';
import { getTokenStore } from '@shared/api/token-store';
import { useAuthStore } from '@shared/stores/auth-store';

export const ME_QUERY_KEY = ['me'] as const;

// Admin-local `useMe`. Mirrors the shape of ecregi-web's hook but uses admin's
// own @tanstack instance (see query-client.ts note for the why).
export function useMe() {
  const setSession = useAuthStore((s) => s.setSession);
  const setHydrated = useAuthStore((s) => s.setHydrated);
  const [hasToken, setHasToken] = useState(() => !!getTokenStore().getAccessToken());
  const [refreshAttempted, setRefreshAttempted] = useState(false);

  useEffect(() => {
    const currentToken = getTokenStore().getAccessToken();
    if (currentToken) {
      setHasToken(true);
      return;
    }
    if (!refreshAttempted) {
      setRefreshAttempted(true);
      refreshAccessToken('admin')
        .then((token) => {
          if (token) {
            setHasToken(true);
          } else {
            setHydrated(true);
          }
        })
        .catch(() => {
          setHydrated(true);
        });
    }
  }, [refreshAttempted, setHydrated]);

  const query = useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: fetchMyProfile,
    enabled: hasToken,
    staleTime: 60_000,
    retry: 1,
  });

  useEffect(() => {
    if (query.data) setSession(query.data);
    if (!hasToken && refreshAttempted) setSession(null);
    if (query.isFetched || (!hasToken && refreshAttempted)) setHydrated(true);
  }, [query.data, query.isFetched, hasToken, refreshAttempted, setSession, setHydrated]);

  return query;
}
